<section class="decision-record" data-testid="your-decision">
  <div *ngIf="isLoading()" class="decision-state" data-testid="decision-loading" role="status">
    {{ decisionText.loadingDecisionRecord() }}
  </div>

  <div *ngIf="!isLoading() && !loadError() && !hasDecision()" class="decision-state decision-state--empty" data-testid="decision-empty">
    <strong>{{ decisionText.noDecisionRecorded() }}</strong>
    <p>{{ hearingText.findingNotFinal() }}</p>
  </div>

  @if (loadError(); as error) { <p role="alert">{{ error }}</p><ui-button severity="secondary" (click)="retryRecord()" [disabled]="isLoading() || externalBusy()">Retry decision</ui-button> }
  <ng-container *ngIf="!isLoading() && hasDecision()">
    <header class="decision-summary" [attr.data-outcome]="bannerTheme()" data-testid="finding-banner">
      <div class="decision-summary__finding">
        <span class="decision-summary__marker" aria-hidden="true"></span>
        <h2 data-testid="banner-heading">{{ bannerHeading() }}</h2>
      </div>
      <div class="decision-summary__status">
        <strong data-testid="final-pill">Final</strong>
        <span data-testid="recorded-meta">
          {{ decisionText.decisionRecorded() }} {{ recordedUtc() | date: "MMM d, y" }} by {{ recorderLabel() }}
        </span>
      </div>
      <div class="decision-summary__actions">
        <ui-button severity="secondary" [disabled]="decisionDocument() === null" (click)="openDecisionPdf()" data-testid="open-pdf">
          {{ decisionText.openDecisionPdf() }}
        </ui-button>
        <span class="decision-document-status" [attr.data-state]="decisionDocument() ? 'available' : 'pending'" data-testid="decision-document-status">
          {{ decisionDocumentStatus() }}
        </span>
        <ui-button #amendTrigger *ngIf="canAmend()" severity="secondary" [disabled]="externalBusy() || isAmending() || amendOpen() || amendmentStale()" [appTerminologyIdentity]="decisionUsage.amendDecision" (click)="openAmend()" data-testid="amend-button">
          {{ decisionText.amendDecision() }}
        </ui-button>
      </div>
    </header>

    <div *ngIf="amendSubmitted()" class="decision-notice decision-notice--success" role="status">
      The amendment was recorded and the decision record was refreshed.
    </div>

    @if (amendmentStale() && !amendOpen()) { <p role="alert">Refresh the current decision before amending again.</p><ui-button #reloadDecisionTrigger severity="secondary" (click)="retryRecord()" [disabled]="isLoading() || externalBusy()">Reload decision</ui-button> }
    <ng-content select="[review-context]"></ng-content>

    <div class="decision-content">
      <div class="decision-content__primary">
        <section class="decision-section" data-testid="rationale-card">
          <h3>{{ decisionText.decisionRationale() }}</h3>
          <p *ngIf="rationale(); else noRationale" class="decision-rationale" data-testid="rationale-text">{{ rationale() }}</p>
          <ng-template #noRationale><p class="decision-empty-value">No rationale recorded.</p></ng-template>
        </section>

        <section class="decision-section" data-testid="determining-factors">
          <h3>Determining Factors</h3>
          <div class="factor-table-wrap">
            <table class="factor-table">
              <thead><tr><th>Factor</th><th>Panel finding</th></tr></thead>
              <tbody>
                <tr *ngFor="let row of determiningFactorRows()" [attr.data-factor]="row.code">
                  <th scope="row">{{ row.label }}</th>
                  <td>{{ row.value }}</td>
                </tr>
                @if (!determiningFactorRows().length) { <tr><td colspan="2">No determining factors were recorded.</td></tr> }
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <aside class="decision-content__terms" aria-label="Decision terms">
        <section class="decision-section decision-section--terms" data-testid="decision-terms">
          <h3>Decision Terms</h3>
          <dl *ngIf="isDenial()" class="term-list">
            <div><dt>Denial length</dt><dd data-testid="rail-denial-length">{{ denialLengthYears() ?? "—" }} years</dd></div>
            <div><dt>{{ hearingText.tentativeNext() }}</dt><dd data-testid="rail-next-hearing">{{ formatDate(tentativeNextHearing()) }}</dd></div>
          </dl>
          <dl *ngIf="isGrant()" class="term-list">
            <div><dt>Proposed release</dt><dd data-testid="rail-release">{{ formatDate(proposedReleaseDate()) }}</dd></div>
            <div><dt>Administrative review</dt><dd data-testid="rail-admin-review">{{ adminReview() ? "Required" : "Not required" }}</dd></div>
            <div *ngIf="governorReview()" class="term-list__attention"><dt>Governor review</dt><dd data-testid="rail-governor-flag">Required for this decision</dd></div>
          </dl>
        </section>

        <section class="decision-section decision-section--conditions" data-testid="special-conditions">
          <h3>{{ hearingText.specialConditions() }}</h3>
          <ul *ngIf="specialConditions().length > 0; else noConditions" class="condition-list">
            <li *ngFor="let condition of specialConditions()">{{ condition }}</li>
          </ul>
          <ng-template #noConditions><p class="decision-empty-value">None.</p></ng-template>
        </section>
      </aside>
    </div>

  </ng-container>
  <ng-template #amendmentTemplate>
    <form id="hearing-amendment-form" class="amend-form" (submit)="$event.preventDefault(); submitAmend()" data-testid="amend-form">
      <p [appTerminologyIdentity]="decisionUsage.amendmentSupersedes">{{ decisionText.amendmentSupersedes() }}</p>
      <div class="finding-row">
        <ui-form [config]="findingConfig" [formValues]="amendValues" [disabled]="!canAmend() || externalBusy() || isAmending() || amendmentStale()" (inputChange)="onAmendInput($event)"></ui-form>
        @if (amendIsUnsuitable()) { <ui-form class="denial" data-testid="amend-denial-length" [config]="denialConfig" [formValues]="amendValues" [disabled]="!canAmend() || externalBusy() || isAmending() || amendmentStale()" (inputChange)="onAmendInput($event)"></ui-form> }
      </div>
      <ui-form data-testid="amend-rationale" [config]="rationaleConfig()" [appTerminologyFormIdentity]="formUsage" [formValues]="amendValues" [disabled]="!canAmend() || externalBusy() || isAmending() || amendmentStale()" (inputChange)="onAmendInput($event)"></ui-form>
      <div><ui-form [config]="conditionsConfig" [formValues]="amendValues" [disabled]="!canAmend() || externalBusy() || isAmending() || amendmentStale()" (inputChange)="onAmendInput($event)"></ui-form><p class="hint">Enter one condition per line.</p></div>
      @if (isAmending()) { <p role="status">Recording amendment...</p> }
      @if (amendError(); as error) { <p class="decision-notice decision-notice--error" role="alert" data-testid="amend-error">{{ error }}</p> }
      @if (amendmentStale()) { <p>The decision changed. Cancel, refresh the decision, and inspect its current wording before amending again.</p> }
    </form>
  </ng-template>
</section>
